import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Alert, CircularProgress, Avatar, MenuItem, TextField,
  LinearProgress, InputAdornment, Tooltip,
} from '@mui/material';
import { FactCheck, Warning, Error as ErrorIcon, Info, CheckCircle, Search } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const SEV_CFG = {
  critical: { color: '#b71c1c', bg: '#ffebee', icon: <ErrorIcon sx={{ fontSize: 14 }} />, label: 'Critical' },
  warning:  { color: '#e65100', bg: '#fff3e0', icon: <Warning   sx={{ fontSize: 14 }} />, label: 'Warning'  },
  info:     { color: '#1565c0', bg: '#e3f2fd', icon: <Info       sx={{ fontSize: 14 }} />, label: 'Info'     },
};

const scoreColor = (s: number) =>
  s >= 90 ? '#2e7d32' : s >= 75 ? '#558b2f' : s >= 60 ? '#f57f17' : s >= 40 ? '#e65100' : '#b71c1c';

const scoreLabel = (s: number) =>
  s >= 90 ? 'Excellent' : s >= 75 ? 'Good' : s >= 60 ? 'Fair' : s >= 40 ? 'Poor' : 'Critical';

interface IssueRow {
  employeeId: string; firstName: string; lastName: string; empNo: string;
  deptName: string; position: string; issueCode: string;
  issueSeverity: 'critical' | 'warning' | 'info'; issueLabel: string;
}

interface Summary {
  totalEmployees: number; affectedEmployees: number; cleanEmployees: number; score: number;
  critical: number; warning: number; info: number;
  byCheck: { label: string; severity: string; count: number }[];
  byDept:  { dept: string; critical: number; warning: number; info: number; affected: number }[];
}

export default function DataQuality() {
  const { user } = useAuth();

  const [tab, setTab]         = useState(0);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [issues, setIssues]   = useState<IssueRow[]>([]);
  const [depts, setDepts]     = useState<any[]>([]);
  const [deptFilter, setDeptFilter] = useState('');
  const [sevFilter, setSevFilter]   = useState('');
  const [codeFilter, setCodeFilter] = useState('');
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (deptFilter) params.set('departmentId', deptFilter);
      const [sum, iss, deptList] = await Promise.all([
        apiFetch(`/api/data-quality/summary?${params}`),
        apiFetch(`/api/data-quality/issues?${params}`),
        apiFetch('/api/departments'),
      ]);
      setSummary(sum);
      setIssues(iss ?? []);
      setDepts(Array.isArray(deptList) ? deptList : (deptList.data ?? []));
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [deptFilter]);

  useEffect(() => { load(); }, [load]);

  const filteredIssues = issues.filter(i => {
    if (sevFilter && i.issueSeverity !== sevFilter) return false;
    if (codeFilter && i.issueCode !== codeFilter) return false;
    if (search) {
      const name = `${i.firstName} ${i.lastName}`.toLowerCase();
      if (!name.includes(search.toLowerCase()) && !i.deptName?.toLowerCase().includes(search.toLowerCase())) return false;
    }
    return true;
  });

  // Group filtered issues by employee
  const byEmployee: Record<string, { name: string; empNo: string; dept: string; position: string; issues: IssueRow[] }> = {};
  filteredIssues.forEach(i => {
    if (!byEmployee[i.employeeId]) {
      byEmployee[i.employeeId] = { name: `${i.firstName} ${i.lastName}`, empNo: i.empNo, dept: i.deptName, position: i.position, issues: [] };
    }
    byEmployee[i.employeeId].issues.push(i);
  });
  const empList = Object.entries(byEmployee).sort(([, a], [, b]) => {
    const sevScore = (issues: IssueRow[]) => issues.filter(i => i.issueSeverity === 'critical').length * 100 + issues.filter(i => i.issueSeverity === 'warning').length * 10;
    return sevScore(b.issues) - sevScore(a.issues);
  });

  const uniqueCodes = [...Object.entries(
    issues.reduce((acc: Record<string, string>, i) => { acc[i.issueCode] = i.issueLabel; return acc; }, {})
  )].sort(([, a], [, b]) => a.localeCompare(b));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ bgcolor: '#37474f', width: 48, height: 48 }}><FactCheck /></Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>HR Data Quality</Typography>
          <Typography variant="body2" color="text.secondary">
            Completeness audit — missing records, expiring documents, data gaps
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : summary && (
        <>
          {/* Score + KPI cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <Paper sx={{ p: 2.5, textAlign: 'center', border: `2px solid ${scoreColor(summary.score)}` }}>
                <Typography variant="h2" fontWeight={800} color={scoreColor(summary.score)}>
                  {summary.score}
                </Typography>
                <Typography variant="body2" fontWeight={600} color={scoreColor(summary.score)}>
                  {scoreLabel(summary.score)}
                </Typography>
                <Typography variant="caption" color="text.secondary">Data Quality Score</Typography>
                <LinearProgress variant="determinate" value={summary.score}
                  sx={{ mt: 1, height: 8, borderRadius: 4,
                    '& .MuiLinearProgress-bar': { bgcolor: scoreColor(summary.score) } }} />
              </Paper>
            </Grid>
            {[
              { label: 'Total Employees',    value: summary.totalEmployees,    color: '#546e7a' },
              { label: 'Employees with Issues', value: summary.affectedEmployees, color: '#e65100' },
              { label: 'Clean Employees',    value: summary.cleanEmployees,    color: '#2e7d32' },
              { label: 'Critical Issues',    value: summary.critical,          color: '#b71c1c' },
              { label: 'Warnings',           value: summary.warning,           color: '#e65100' },
              { label: 'Info Items',         value: summary.info,              color: '#1565c0' },
            ].map(s => (
              <Grid size={{ xs: 6, sm: 4, md: 1.5 }} key={s.label}>
                <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>{s.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {summary.critical > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>{summary.critical} critical issues</strong> require immediate attention — missing bank accounts, expired contracts, or expired documents.
            </Alert>
          )}

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Issues by Employee" />
            <Tab label="Issues by Check Type" />
            <Tab label="By Department" />
          </Tabs>

          {/* Shared filters */}
          <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder="Search employee / dept…" value={search}
              onChange={e => setSearch(e.target.value)} sx={{ minWidth: 200 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            <TextField select size="small" label="Department" value={deptFilter}
              onChange={e => setDeptFilter(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All Departments</MenuItem>
              {depts.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
            {tab === 0 && (
              <>
                <TextField select size="small" label="Severity" value={sevFilter}
                  onChange={e => setSevFilter(e.target.value)} sx={{ minWidth: 130 }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="critical">🔴 Critical</MenuItem>
                  <MenuItem value="warning">🟡 Warning</MenuItem>
                  <MenuItem value="info">🔵 Info</MenuItem>
                </TextField>
                <TextField select size="small" label="Issue Type" value={codeFilter}
                  onChange={e => setCodeFilter(e.target.value)} sx={{ minWidth: 220 }}>
                  <MenuItem value="">All Types</MenuItem>
                  {uniqueCodes.map(([code, label]) => <MenuItem key={code} value={code}>{label}</MenuItem>)}
                </TextField>
              </>
            )}
          </Paper>

          {/* ── By Employee ── */}
          {tab === 0 && (
            empList.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CheckCircle sx={{ fontSize: 64, color: '#2e7d32', opacity: 0.4, mb: 1 }} />
                <Typography color="text.secondary">No issues found matching your filters 🎉</Typography>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Position</TableCell>
                      <TableCell>Issues</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {empList.map(([empId, emp]) => {
                      const crit = emp.issues.filter(i => i.issueSeverity === 'critical').length;
                      const warn = emp.issues.filter(i => i.issueSeverity === 'warning').length;
                      const inf  = emp.issues.filter(i => i.issueSeverity === 'info').length;
                      return (
                        <TableRow key={empId} hover sx={{ bgcolor: crit > 0 ? '#ffebee15' : undefined, verticalAlign: 'top' }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{emp.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{emp.empNo}</Typography>
                          </TableCell>
                          <TableCell><Typography variant="body2">{emp.dept}</Typography></TableCell>
                          <TableCell><Typography variant="body2">{emp.position}</Typography></TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {emp.issues.map((issue, idx) => {
                                const cfg = SEV_CFG[issue.issueSeverity];
                                return (
                                  <Tooltip key={idx} title={issue.issueLabel}>
                                    <Chip size="small"
                                      icon={<Box sx={{ display: 'flex', color: cfg.color }}>{cfg.icon}</Box>}
                                      label={issue.issueLabel}
                                      sx={{ bgcolor: cfg.bg, color: cfg.color, fontSize: '0.6rem', maxWidth: 220,
                                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                                  </Tooltip>
                                );
                              })}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                              {crit > 0 && <Chip size="small" label={`${crit} critical`} sx={{ bgcolor: '#ffebee', color: '#b71c1c', fontSize: '0.6rem' }} />}
                              {warn > 0 && <Chip size="small" label={`${warn} warning`} sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '0.6rem' }} />}
                              {inf  > 0 && <Chip size="small" label={`${inf} info`}     sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.6rem' }} />}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}

          {/* ── By Check Type ── */}
          {tab === 1 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Issue</TableCell>
                    <TableCell align="center">Severity</TableCell>
                    <TableCell align="right">Affected Employees</TableCell>
                    <TableCell>Coverage Bar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.byCheck.map((c, idx) => {
                    const cfg = SEV_CFG[c.severity as keyof typeof SEV_CFG] ?? SEV_CFG.info;
                    const maxCount = Math.max(...summary.byCheck.map(x => x.count), 1);
                    return (
                      <TableRow key={idx} hover>
                        <TableCell><Typography variant="body2">{c.label}</Typography></TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={cfg.label}
                            sx={{ bgcolor: cfg.bg, color: cfg.color, fontSize: '0.65rem' }} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700} color={cfg.color}>{c.count}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <LinearProgress variant="determinate" value={Math.round((c.count / maxCount) * 100)}
                            sx={{ height: 8, borderRadius: 4,
                              '& .MuiLinearProgress-bar': { bgcolor: cfg.color } }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {summary.byCheck.length === 0 && (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      <CheckCircle sx={{ mr: 1, color: '#2e7d32', verticalAlign: 'middle' }} />
                      No issues found — great data quality! 🎉
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── By Department ── */}
          {tab === 2 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">🔴 Critical</TableCell>
                    <TableCell align="right">🟡 Warning</TableCell>
                    <TableCell align="right">🔵 Info</TableCell>
                    <TableCell align="right">Affected Employees</TableCell>
                    <TableCell>Issue Breakdown</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.byDept.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No issues by department</TableCell></TableRow>
                  )}
                  {summary.byDept.map((d, idx) => {
                    const total = d.critical + d.warning + d.info;
                    return (
                      <TableRow key={idx} hover sx={{ bgcolor: d.critical > 0 ? '#ffebee10' : undefined }}>
                        <TableCell><Typography variant="body2" fontWeight={600}>{d.dept}</Typography></TableCell>
                        <TableCell align="right">
                          {d.critical > 0 ? <Typography fontWeight={700} color="#b71c1c">{d.critical}</Typography> : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {d.warning > 0 ? <Typography fontWeight={600} color="#e65100">{d.warning}</Typography> : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {d.info > 0 ? <Typography color="#1565c0">{d.info}</Typography> : '—'}
                        </TableCell>
                        <TableCell align="right">{d.affected}</TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <Box sx={{ display: 'flex', height: 16, borderRadius: 1, overflow: 'hidden', gap: '1px' }}>
                            {d.critical > 0 && <Box sx={{ flex: d.critical, bgcolor: '#b71c1c', minWidth: 4 }} />}
                            {d.warning  > 0 && <Box sx={{ flex: d.warning,  bgcolor: '#f57f17', minWidth: 4 }} />}
                            {d.info     > 0 && <Box sx={{ flex: d.info,     bgcolor: '#1565c0', minWidth: 4 }} />}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}
